import { useState, useCallback, useEffect } from 'react';
import { apiService } from '../../services';

interface EntityCounts {
  drafted: number;
  published: number;
}

interface EntityStats {
  articles: EntityCounts;
  events: EntityCounts;
  albums: EntityCounts;
  canticles: EntityCounts;
  books: EntityCounts;
}

export const useEntityStats = () => {
  const [stats, setStats] = useState<EntityStats>({
    articles: { drafted: 0, published: 0 },
    events: { drafted: 0, published: 0 },
    albums: { drafted: 0, published: 0 },
    canticles: { drafted: 0, published: 0 },
    books: { drafted: 0, published: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all items (published_only=false) to count drafted vs published
      const [articlesRes, eventsRes, albumsRes, canticlesRes, booksRes] =
        await Promise.all([
          apiService.getArticles(0, 1000, false),
          apiService.getEvents(0, 1000, false),
          apiService.getAlbums(),
          apiService.getCanticles(0, 1000, false),
          apiService.getBooks(0, 1000, false),
        ]);

      // Count drafted vs published for each entity type
      const articles = articlesRes.articles || [];
      const events = eventsRes.events || [];
      const albums = albumsRes.albums || [];
      const canticles = Array.isArray(canticlesRes) ? canticlesRes : [];
      const books = Array.isArray(booksRes) ? booksRes : [];

      setStats({
        articles: {
          drafted: articles.filter((a) => a.status === 'draft').length,
          published: articles.filter((a) => a.status === 'published').length,
        },
        events: {
          drafted: events.filter((e) => e.status === 'draft').length,
          published: events.filter((e) => e.status === 'published').length,
        },
        albums: {
          drafted: albums.filter((a) => a.status === 'draft').length,
          published: albums.filter((a) => a.status === 'published').length,
        },
        canticles: {
          drafted: canticles.filter((c) => c.status === 'draft').length,
          published: canticles.filter((c) => c.status === 'published').length,
        },
        books: {
          drafted: books.filter((b) => !b.is_published).length,
          published: books.filter((b) => b.is_published).length,
        },
      });
    } catch (err) {
      console.error('Failed to fetch entity stats:', err);
      setError('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refresh: fetchStats,
  };
};
